(ns yardwerkz.web.effects
  "RFX effect handlers for declarative side effects.
   Uses Capra RPC for all backend communication."
  (:require
   [lambdaisland.glogi :as log]
   [io.factorhouse.rfx.core :as rfx]
   [capra.core :as capra]
   [yardwerkz.capra-client :as capra-client]
   [yardwerkz.capra-dispatch :as capra-dispatch]
   [yardwerkz.api-helpers :as api-h]))

;; =============================================================================
;; Token Provider
;; =============================================================================

(defonce token-provider (atom nil))

(defn set-token-provider!
  "Set the Clerk getToken function. Called during auth setup."
  [get-token-fn]
  (reset! token-provider get-token-fn))

;; =============================================================================
;; HTTP Batch Request Effect (Capra-backed)
;; =============================================================================

(defn http-batch-request-fx [rfx-ctx effect-data]
  (let [{:keys [action data success-event failure-event on-success on-failure]} effect-data]
    (when-not action
      (throw (ex-info "http/batch-request effect requires :action" {:received effect-data})))
    (log/debug :http-batch-request-effect {:action action})
    (-> (api-h/request!
         @token-provider
         (fn [ctx identity]
           ;; Call the action keyword directly via capra/call — this works
           ;; for any action the server handles, regardless of whether
           ;; gen-stubs generated a named function for it.
           (capra/call ctx nil action (assoc (or data {}) :auth identity))))
        (.then (fn [result]
                 ;; Capra returns the raw result, not wrapped in {:ok :data}.
                 ;; Wrap it to match the existing store's expectations.
                 (let [wrapped {:ok true :data result}]
                   (when success-event
                     (rfx/dispatch rfx-ctx (conj success-event wrapped)))
                   (when on-success
                     (on-success wrapped)))))
        (.catch (fn [error]
                  (log/error :http-batch-request-error
                             {:action action :error (ex-message error)})
                  (let [error-data {:code (or (:code (ex-data error)) "NETWORK_ERROR")
                                    :message (or (ex-message error) "Request failed")}]
                    (when failure-event
                      (rfx/dispatch rfx-ctx (conj failure-event error-data)))
                    (when on-failure
                      (on-failure error-data))))))))

;; =============================================================================
;; HTTP Batch Multi-Request Effect (Capra-backed)
;; =============================================================================

(defn http-batch-multi-fx [rfx-ctx {:keys [requests success-event failure-event on-success on-failure]}]
  (when-not (seq requests)
    (throw (ex-info "http/batch-multi effect requires non-empty :requests" {})))
  (log/debug :http-batch-multi-effect
             {:count (count requests) :actions (mapv :action requests)})
  (-> (api-h/request!
       @token-provider
       (fn [ctx identity]
         ;; Build a vector of PendingRefs — one per request — for batch execution
         (mapv (fn [{:keys [action data]}]
                 (capra/call ctx nil action (assoc (or data {}) :auth identity)))
               requests)))
      (.then (fn [results]
               ;; results is a vector of values (one per request)
               (let [wrapped {:ok true :data results}]
                 (when success-event
                   (rfx/dispatch rfx-ctx (conj success-event wrapped)))
                 (when on-success
                   (on-success wrapped)))))
      (.catch (fn [error]
                (log/error :http-batch-multi-error
                           {:count (count requests) :error (ex-message error)})
                (let [error-data {:code "NETWORK_ERROR" :message (ex-message error)}]
                  (when failure-event
                    (rfx/dispatch rfx-ctx (conj failure-event error-data)))
                  (when on-failure
                    (on-failure error-data)))))))

;; =============================================================================
;; Realtime Subscribe/Unsubscribe Effects (Capra-backed)
;; =============================================================================

(defonce ^:private active-subs (atom {}))

(defn realtime-subscribe-fx
  [rfx-ctx {:keys [sub-id type id success-event on-update-event]}]
  (-> (capra-dispatch/capra-subscribe!
       @token-provider type id
       (fn [data]
         (when on-update-event
           (rfx/dispatch rfx-ctx (conj on-update-event data)))))
      (.then (fn [{:keys [data stub]}]
               (swap! active-subs assoc sub-id stub)
               (log/debug :realtime-fx/subscribed {:sub-id sub-id :type type})
               (when success-event
                 (rfx/dispatch rfx-ctx (conj success-event data)))))
      (.catch (fn [err]
                (log/error :realtime-fx/subscribe-error
                           {:sub-id sub-id :type type :error (ex-message err)})))))

(defn realtime-unsubscribe-fx [_rfx-ctx {:keys [sub-id]}]
  (when-let [stub (get @active-subs sub-id)]
    (capra-dispatch/capra-unsubscribe! stub)
    (swap! active-subs dissoc sub-id)))

;; =============================================================================
;; Dispatch Later + Async Effects
;; =============================================================================

(defn dispatch-later-fx [rfx-ctx {:keys [ms dispatch]}]
  (js/setTimeout #(rfx/dispatch rfx-ctx dispatch) ms))

(defn async-fx
  [rfx-ctx {:keys [call success-event failure-event on-success on-failure]}]
  (when-not call
    (throw (ex-info "async effect requires :call" {})))
  (-> (call)
      (.then (fn [result]
               (if (:ok result)
                 (do
                   (when success-event
                     (rfx/dispatch rfx-ctx (conj success-event result)))
                   (when on-success
                     (on-success result)))
                 (do
                   (when failure-event
                     (rfx/dispatch rfx-ctx (conj failure-event (:error result))))
                   (when on-failure
                     (on-failure (:error result)))))))
      (.catch (fn [error]
                (log/error :async-effect-error {:error (ex-message error)})
                (let [error-data {:code "NETWORK_ERROR"
                                  :message (or (ex-message error)
                                               (.-message error)
                                               "Unknown error")}]
                  (when failure-event
                    (rfx/dispatch rfx-ctx (conj failure-event error-data)))
                  (when on-failure
                    (on-failure error-data)))))))

;; =============================================================================
;; Effect Registration
;; =============================================================================

(defn register-effects! [reg-fx-fn]
  (log/info :batch-effects/registering {})
  (reg-fx-fn :http/batch-request http-batch-request-fx)
  (reg-fx-fn :http/batch-multi http-batch-multi-fx)
  (reg-fx-fn :dispatch-later dispatch-later-fx)
  (reg-fx-fn :realtime/subscribe realtime-subscribe-fx)
  (reg-fx-fn :realtime/unsubscribe realtime-unsubscribe-fx)
  (reg-fx-fn :async async-fx)
  (reg-fx-fn :callback (fn [_rfx-ctx f] (when f (f))))
  (log/info :batch-effects/registered {}))

;; =============================================================================
;; Helper Functions
;; =============================================================================

(defn batch-request-effect
  ([action success-event]
   (batch-request-effect action {} success-event nil))
  ([action data success-event]
   (batch-request-effect action data success-event nil))
  ([action data success-event failure-event]
   {:action action :data data :success-event success-event :failure-event failure-event}))

(defn batch-multi-effect
  ([requests success-event]
   (batch-multi-effect requests success-event nil))
  ([requests success-event failure-event]
   {:requests requests :success-event success-event :failure-event failure-event}))
